// [PURPOSE]: Validate and sanitize localStorage on app startup
// [WHY]: Corrupted localStorage data causes "Cannot convert null to object" crashes
// [CREATED]: November 3, 2025 - Session 6: Comprehensive localStorage validation
// [FIXED]: November 4, 2025 - Session 7: Use CORRECT keys and validate data STRUCTURE

/**
 * Validate game state structure
 * @param {object} state - Parsed game state object
 * @returns {object} - { valid: boolean, errors: string[] }
 */
function validateGameStateStructure(state) {
  const errors = [];

  // Must be an object
  if (!state || typeof state !== 'object' || Array.isArray(state)) {
    errors.push('Game state must be an object');
    return { valid: false, errors };
  }

  // Required top-level fields with type checks
  if (!state.playerStats || typeof state.playerStats !== 'object' || Array.isArray(state.playerStats)) {
    errors.push('playerStats must be an object');
  }

  if (state.inventory !== undefined && !Array.isArray(state.inventory)) {
    errors.push('inventory must be an array');
  }

  if (state.vaults !== undefined && !Array.isArray(state.vaults)) {
    errors.push('vaults must be an array');
  }

  if (state.currencies !== undefined && (typeof state.currencies !== 'object' || Array.isArray(state.currencies))) {
    errors.push('currencies must be an object');
  }

  // Required nested fields in playerStats
  if (state.playerStats) {
    if (typeof state.playerStats.level !== 'number') {
      errors.push('playerStats.level must be a number');
    }
    if (!Array.isArray(state.playerStats.classes)) {
      errors.push('playerStats.classes must be an array');
    }

    // [v1.0]: Validate class/race names resolve via contentResolver
    // Uses lazy require to avoid circular dependency issues at startup
    try {
      const { getClassData, getRaceData } = require('./contentResolver');

      // Validate class names exist in SRD or custom sources
      if (Array.isArray(state.playerStats.classes)) {
        state.playerStats.classes.forEach((cls, idx) => {
          if (cls.className && !getClassData(cls.className)) {
            // Warn but don't block — the class source might not be enabled yet
            console.warn(`⚠️ storageValidator: classes[${idx}].className "${cls.className}" not found in SRD or custom sources`);
          }
        });
      }

      // Validate race name exists
      if (state.playerStats.basics?.race) {
        if (!getRaceData(state.playerStats.basics.race)) {
          console.warn(`⚠️ storageValidator: basics.race "${state.playerStats.basics.race}" not found in SRD or custom sources`);
        }
      }
    } catch (e) {
      // contentResolver not available yet during startup — skip validation
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate all localStorage data and clear if corrupted
 *
 * [FIXED NOV 4]: Now checks ACTUAL keys used by the app, not old architecture keys
 *
 * @returns {object} Validation result with status and messages
 */
export function validateAndSanitizeStorage() {
  const results = {
    valid: true,
    cleared: [],
    errors: [],
    warnings: []
  };


  // ============================================================================
  // ACTUAL localStorage keys used by current app architecture
  // ============================================================================
  const simpleKeys = {
    'tabletopInventory_settings': { type: 'object', required: false },
    'savedCharacters': { type: 'array', required: false },
    'lastCharacterId': { type: 'string', required: false },
    'partyId': { type: 'string', required: false },
    'partyName': { type: 'string', required: false },
    'quickMenuPosition': { type: 'object', required: false },
    'collapsedSpellLevels': { type: 'object', required: false },
    // [FIX APR 2026 Phase 3A.4]: customComponents is an array (see CharacterContext.js:124
    // init with [], App.jsx:3688 spreads it, CustomSpellCreator.js default []).
    // Previously schema said 'object', causing validator to clear data on every load
    // → infinite "corrupt localStorage" loop.
    'customComponents': { type: 'array', required: false }
  };

  // ============================================================================
  // Validate simple keys (JSON parse + type check only)
  // ============================================================================
  for (const [key, schema] of Object.entries(simpleKeys)) {
    try {
      const value = localStorage.getItem(key);

      if (value === null || value === undefined) {
        // Key doesn't exist - that's fine unless required
        if (schema.required) {
          results.warnings.push(`${key}: missing (required)`);
        }
        continue;
      }

      // Try to parse if it looks like JSON
      if (value.startsWith('{') || value.startsWith('[')) {
        try {
          const parsed = JSON.parse(value);

          // Validate parsed data is not null
          if (parsed === null) {
            results.warnings.push(`${key}: parsed to null`);
            localStorage.removeItem(key);
            results.cleared.push(key);
            results.valid = false;
            continue;
          }

          // Type validation
          if (schema.type === 'object' && (typeof parsed !== 'object' || Array.isArray(parsed))) {
            results.errors.push(`${key}: expected object, got ${Array.isArray(parsed) ? 'array' : typeof parsed}`);
            localStorage.removeItem(key);
            results.cleared.push(key);
            results.valid = false;
            continue;
          }

          if (schema.type === 'array' && !Array.isArray(parsed)) {
            results.errors.push(`${key}: expected array, got ${typeof parsed}`);
            localStorage.removeItem(key);
            results.cleared.push(key);
            results.valid = false;
            continue;
          }

          // Test if object/array can be iterated (detects corruption)
          try {
            if (typeof parsed === 'object') {
              Object.keys(parsed);
            }
          } catch (err) {
            results.errors.push(`${key}: corrupt object/array (${err.message})`);
            localStorage.removeItem(key);
            results.cleared.push(key);
            results.valid = false;
          }

        } catch (parseError) {
          results.errors.push(`${key}: JSON parse failed (${parseError.message})`);
          localStorage.removeItem(key);
          results.cleared.push(key);
          results.valid = false;
        }
      }
    } catch (error) {
      results.errors.push(`${key}: validation error (${error.message})`);
      localStorage.removeItem(key);
      results.cleared.push(key);
      results.valid = false;
    }
  }

  // ============================================================================
  // Special validation for tabletopInventory_autosave (CRITICAL)
  // ============================================================================
  try {
    const autosaveValue = localStorage.getItem('tabletopInventory_autosave');

    if (autosaveValue) {

      try {
        const gameState = JSON.parse(autosaveValue);

        // Validate structure
        const validation = validateGameStateStructure(gameState);

        if (!validation.valid) {
          results.errors.push(`tabletopInventory_autosave: Invalid structure - ${validation.errors.join(', ')}`);
          localStorage.removeItem('tabletopInventory_autosave');
          results.cleared.push('tabletopInventory_autosave');
          results.valid = false;
          console.error('❌ Autosave has invalid structure:', validation.errors);
        }

      } catch (parseError) {
        results.errors.push(`tabletopInventory_autosave: JSON parse failed (${parseError.message})`);
        localStorage.removeItem('tabletopInventory_autosave');
        results.cleared.push('tabletopInventory_autosave');
        results.valid = false;
      }
    }
  } catch (error) {
    results.errors.push(`tabletopInventory_autosave: validation error (${error.message})`);
    localStorage.removeItem('tabletopInventory_autosave');
    results.cleared.push('tabletopInventory_autosave');
    results.valid = false;
  }

  // ============================================================================
  // Log results
  // ============================================================================
  if (!results.valid) {
    console.warn('⚠️ localStorage had issues:', {
      cleared: results.cleared.length,
      errors: results.errors.length,
      warnings: results.warnings.length
    });

    if (results.errors.length > 0) {
      console.error('❌ Errors:', results.errors);
    }

    if (results.warnings.length > 0) {
      console.warn('⚠️ Warnings:', results.warnings);
    }
  }

  return results;
}

/**
 * Emergency: Clear ALL localStorage if validation fails catastrophically
 */
export function emergencyClearStorage() {
  console.error('🚨 EMERGENCY: Clearing ALL localStorage');

  const backup = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    backup[key] = localStorage.getItem(key);
  }


  localStorage.clear();


  return backup;
}

/**
 * [FIX P4-7]: Safe localStorage write with QuotaExceededError handling
 * [WHY]: Unhandled quota errors crash the app silently on mobile devices with limited storage
 * @param {string} key - localStorage key
 * @param {string} value - Value to store (already stringified)
 * @returns {boolean} true if write succeeded
 */
export function safeLocalStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError' || error.code === 22 || error.code === 1014) {
      console.error(`[Storage] Quota exceeded for key "${key}" (${Math.round((value?.length || 0) / 1024)}KB)`);
    } else {
      console.error(`[Storage] Failed to write "${key}":`, error.message);
    }
    return false;
  }
}

/**
 * Check if localStorage is accessible
 */
export function isStorageAccessible() {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}
